import {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {resolveEffectivePermissions, resolveUnionPermissions, hasPermissionInAnyBranch} from "./Helpers";

const usePermissions = () => {
    const user = useSelector((state) => state.user.user);
    const activeLocationId = useSelector((state) => state.userSettings.scheduleLocation?.id);
    const [allPermissions, setAllPermissions] = useState(resolveEffectivePermissions(user, activeLocationId));
    const [unionPermissions, setUnionPermissions] = useState(resolveUnionPermissions(user));

    useEffect(() => {
        setAllPermissions(resolveEffectivePermissions(user, activeLocationId));
        setUnionPermissions(resolveUnionPermissions(user));
    }, [user, activeLocationId]);

    const hasAny = useCallback(
        (permissions) => Array.isArray(permissions) && permissions.some(p => allPermissions.includes(p)),
        [allPermissions]
    );

    const has = useCallback(
        (permission) => Array.isArray(permission) ? hasAny(permission) : allPermissions.includes(permission),
        [allPermissions, hasAny]
    );

    const hasLock = useCallback(
        (permission) => typeof permission === 'string'
            ? allPermissions.includes(`${permission}Lock`)
            : (Array.isArray(permission) && hasAny(permission?.map(p => `${p}Lock`))),
        [allPermissions, hasAny]
    );

    const hasAll = useCallback(
        (permissions) => Array.isArray(permissions) && permissions.every(p => allPermissions.includes(p)),
        [allPermissions]
    );

    const hasWithout = useCallback(
        (permission, withoutPermission) => has(permission) && !has(withoutPermission),
        [has]
    );

    const hasInAnyBranch = useCallback((permissions) => {
        const list = Array.isArray(permissions) ? permissions : [permissions];
        return list.some(p => {
            if (!user?.network_management) return allPermissions.includes(p);
            return hasPermissionInAnyBranch(p, user);
        });
    }, [user, allPermissions]);

    // Menu visibility: union across all branches in network mode (show as much as possible).
    // Action/click enforcement still uses active-branch `has` / `hasLock`.
    const hasAnyVisible = useCallback(
        (permissions) => Array.isArray(permissions) && permissions.some(p => unionPermissions.includes(p)),
        [unionPermissions]
    );

    const hasVisible = useCallback(
        (permission) => Array.isArray(permission)
            ? hasAnyVisible(permission)
            : unionPermissions.includes(permission),
        [unionPermissions, hasAnyVisible]
    );

    const hasAllVisible = useCallback(
        (permissions) => Array.isArray(permissions) && permissions.every(p => unionPermissions.includes(p)),
        [unionPermissions]
    );

    // Whole-box settings: true for non-network boxes, or network box-wide BOXADMIN.
    const canEditBoxWideSettings = useCallback(() => {
        if (!user?.network_management) return true;
        return !!user.can_edit_box_wide_settings;
    }, [user]);

    // Permission expansion across all branches (any box-wide role). Not settings.
    const isBoxWide = useCallback(() => {
        if (!user?.network_management) return true;
        return !!user.is_box_wide;
    }, [user]);

    return { has, hasAny, hasAll, hasLock, hasWithout, hasInAnyBranch, hasVisible, hasAnyVisible, hasAllVisible, isBoxWide, canEditBoxWideSettings };
}

export default usePermissions;
